// The render-plugin seam (browser). Keeps the agentEventReducer and chat
// components generic: application-specific event types (table_rendered,
// chart_rendered, dashboard_created, webapp_ready, ...) are handled by registered
// plugins rather than inline in the core. Platinum's Carbon table/chart/dashboard
// widgets register here. See ../../docs/09-frontend-components.md.

import type { ReactNode } from "react";

/** One server-sent event as seen by the browser (mirror of the Go events.Envelope
 *  and the sandbox AgentEvent). */
export interface AgentSSEEvent {
  type: string;
  data: Record<string, unknown>;
  timestamp?: string;
}

/** A render plugin owns one or more extension event types: it folds them into
 *  plugin-scoped state (kept in a side-channel keyed by toolCallId/messageId, so
 *  the core reducer state stays generic) and renders them inline. */
export interface RenderPlugin<TState = unknown> {
  /** Extension event types this plugin handles, e.g. ["table_rendered"]. */
  eventTypes: string[];
  /** Initial plugin state. */
  init(): TState;
  /** Fold a plugin event into plugin-scoped state. Pure — replay-safe. */
  reduce(state: TState, event: AgentSSEEvent): TState;
  /** Render the plugin's artifact inline, attached to a tool call. */
  render(props: { state: TState; toolCallId: string; sessionId: string }): ReactNode;
}

/** A tool formatter customises how a tool call is labelled/summarised in the UI
 *  (e.g. Platinum's `pt` CLI command parsing). Generic formatters ship in core. */
export interface ToolFormatter {
  /** Return a display name for a tool call, or undefined to fall through. */
  displayName?(toolName: string, input: unknown): string | undefined;
  /** Return a one-line summary, or undefined to fall through. */
  summary?(toolName: string, input: unknown): string | undefined;
}

/** One host-supplied tab in the left side panel (<AgentSidePanel>). Host tabs are
 *  rendered after the built-in History tab, in array order, and the first one is
 *  the panel's default tab. `render` is called on every panel render, so hosts
 *  should return an element rather than doing work here. */
export interface AgentSidePanelTab {
  /** Stable identity for the tab. */
  key: string;
  /** Tab strip label. */
  label: string;
  /** Glyph for this tab on the <SidePanelLauncher> dial. Optional: without one
   *  the dial shows the label's first letter. The tab strip itself stays words. */
  icon?: ReactNode;
  /** Tab body. */
  render(): ReactNode;
}

/** One chip pinned to the composer by the host (e.g. a table the user picked
 *  from a side-panel tab). Chips are not editable: remove one and add another. */
export interface AgentComposerChip {
  id: string;
  label: string;
  /** Hover text, e.g. the full path the label was shortened from. */
  tooltip?: string;
}

/** Host control over the message composer. When absent, the composer behaves
 *  exactly as it does with no host involvement: no chip row, and the text the
 *  user typed is the text that is sent. */
/**
 * An imperative handle on the composer for hosts that drive it from outside
 * the chat — a voice assistant typing on the user's behalf, a "try this"
 * button. Read live: `getInput` is the current text, `setInput` replaces it
 * through the same setter the textarea uses, `send` submits exactly as the
 * Send button would (same guards: nothing while streaming or disabled).
 */
export interface AgentComposerHandle {
  getInput: () => string
  setInput: (text: string) => void
  send: () => Promise<void>
  /** The model the next message will use, and the choices the toggle offers. */
  getModel: () => string
  setModel: (id: string) => void
  models: () => Array<{ id: string; label: string }>
}

export interface AgentComposerConfig {
  /** Chips rendered as a row above the input, in this order. */
  chips: AgentComposerChip[];
  /** Called when the user deletes a chip. The host owns chip state. */
  onRemoveChip(id: string): void;
  /**
   * Called when the user clicks a chip's face (not its delete icon). Optional:
   * with no handler the chip is inert text, which is the previous behaviour.
   * The host decides what a click means — Platinum opens a preview of whatever
   * the chip points at.
   */
  onChipClick?(id: string): void;
  /**
   * A starting point for the message, offered when the host thinks one would
   * help — Platinum offers one the moment the first chip is attached.
   *
   * 🔴 It is only ever adopted into an EMPTY box, and only once per distinct
   * value. What the user has typed is theirs: attaching something must never
   * overwrite, append to, or reorder it. Set it back to '' to re-arm.
   */
  suggestedText?: string;
  /** Maps the typed text to the text actually sent (e.g. appends a trailing
   *  block naming the chipped items). Called once per send. */
  buildMessage(content: string): string;
  /** Called after the message has been handed to the send path — the host's cue
   *  to clear its chips. Fires alongside the input being cleared, not after the
   *  agent has replied. */
  onSent?(): void;
}

/** Renders one run of plain text from a message, or returns it untouched. */
export type InlineTextRenderer = (text: string) => ReactNode;

/**
 * Host hook for inline citation markers in assistant markdown.
 *
 * Called once per rendered message with that message's whole markdown, so the
 * host can build its numbering before any text node is rendered, and returns
 * the renderer applied to each plain-text node. Return undefined (or leave the
 * hook unset) and the markdown renders exactly as it does today.
 *
 * Deliberately shaped around plain text, not around any citation syntax: the
 * pattern, the marker and what a click does are all the host's business.
 */
export type InlineCitationRendererFactory = (content: string) => InlineTextRenderer | undefined;

/**
 * Draw the conversation as a sheet rather than as a full-width column.
 *
 * The messages and the composer share one centred column of at most `maxWidth`,
 * with rounded top corners and a soft top shadow, running to the bottom edge —
 * a panel sitting on the page's own ground. Hosts whose previous screen drew a
 * column of the same width get an arrival that reads as that column rising into
 * place. Leave it unset and the layout is unchanged.
 */
export interface AgentChatSheetConfig {
  /** The sheet's width cap, in pixels. Match the previous screen's column. */
  maxWidth: number;
  /** Let the column fill the whole width instead (a reader's choice the host
   *  remembers); the composer keeps its card. */
  fullWidth?: boolean;
  /** Draw the composer card with a straight top edge — no rounded corners.
   *  Platinum sets it while BOTH side panels are open: the card then meets
   *  the panels rather than sitting in open ground (Adam, 10 Sep). */
  squareTop?: boolean;
}

/** Everything a host injects into <AgentChatProvider>. */
export interface AgentChatConfig {
  apiBaseUrl: string;
  /**
   * Models offered in the picker, in display order.
   *
   * `costMultiplier` is optional cost relative to whatever the host treats as
   * its baseline model (1 = baseline, 2 = twice as expensive, 0.4 = 40% of it).
   * When present it is shown beside the label so the cost of a choice is
   * visible at the moment of choosing. Omit it and the picker shows labels only.
   */
  models: { id: string; label: string; costMultiplier?: number }[];
  /**
   * Model selected when a session starts. Falls back to `models[0]` when unset.
   * Explicit because display order and the sensible default are different
   * questions — a list ordered newest-first should not force the newest model.
   */
  defaultModel?: string;
  plugins?: RenderPlugin[];
  toolFormatters?: ToolFormatter[];
  endpoints?: Partial<AgentChatEndpoints>;
  /** Extra tabs for the left side panel, rendered before History. */
  sidePanelTabs?: AgentSidePanelTab[];
  /** Composer chips + outgoing-message shaping. */
  composer?: AgentComposerConfig;
  /** Inline citation markers in assistant markdown. See the type. */
  renderInlineCitations?: InlineCitationRendererFactory;
  /** Draw the conversation as a centred sheet. See the type. */
  sheet?: AgentChatSheetConfig;
  /** Async or sync token provider — used by Provider contexts for session-list API calls. */
  getAuthToken?: () => Promise<string> | string;
  onToolResult?: (toolCallId: string, output: unknown) => void;
  onSessionTitle?: (sessionId: string, title: string) => void;
  onArtifactsUpdated?: (sessionId: string) => void;
  /** An agent turn ended, with its status. Refresh what the agent may have changed. */
  onTurnComplete?: (sessionId: string, status: string | undefined) => void;
  /**
   * The activity label while the chat waits for a server that went away (a
   * release restarting it). Defaults to "Reconnecting to Platinum — your chat
   * will carry on"; a host that knows a release is under way can say so.
   */
  reconnectLabel?: string;
}

/** Endpoint paths, overridable; defaults match the conventional /agent/* shape. */
export interface AgentChatEndpoints {
  createSession: string;                        // POST
  sendMessage: (sessionId: string) => string;   // POST (SSE)
  reconnect: (sessionId: string) => string;     // GET (SSE)
  status: (sessionId: string) => string;        // GET
  cancel: (sessionId: string) => string;        // POST
  pageToolResult: (sessionId: string) => string; // POST
  getSession: (sessionId: string) => string;    // GET
  deleteSession: (sessionId: string) => string; // DELETE
  messages: (sessionId: string) => string;      // GET
  queryEvents: (sessionId: string) => string;   // GET
  artifacts: (sessionId: string) => string;     // GET/POST
  upload: (sessionId: string) => string;        // POST
  listSessions: string;                         // GET
  searchMessages: string;                       // GET
  /** GET: a Chief of Staff chat's workflow run, its agents read from the saved files. */
  workflowRun: (sessionId: string, runId: string) => string;
  /** GET: one workflow agent's saved conversation (404 when no longer saved). */
  workflowTranscript: (sessionId: string, runId: string, agentId: string) => string;
}

export const DEFAULT_ENDPOINTS: AgentChatEndpoints = {
  createSession: "/agent/session",
  sendMessage: (id) => `/agent/session/${id}/message`,
  reconnect: (id) => `/agent/session/${id}/reconnect`,
  status: (id) => `/agent/session/${id}/status`,
  cancel: (id) => `/agent/session/${id}/cancel`,
  pageToolResult: (id) => `/agent/session/${id}/page-tool-result`,
  getSession: (id) => `/agent/session/${id}`,
  deleteSession: (id) => `/agent/session/${id}`,
  messages: (id) => `/agent/session/${id}/messages`,
  queryEvents: (id) => `/agent/session/${id}/query-events`,
  artifacts: (id) => `/agent/session/${id}/artifacts`,
  upload: (id) => `/agent/session/${id}/upload`,
  listSessions: "/agent/sessions",
  searchMessages: "/agent/messages/search",
  workflowRun: (id, run) => `/agent/session/${id}/workflows/${run}`,
  workflowTranscript: (id, run, agent) => `/agent/session/${id}/workflows/${run}/agents/${agent}`,
};

// The AgentChatProvider, the reducer's plugin dispatch, and the components are
// part of the frontend copy per ../../docs/90-provenance-map.md.
